//Imports
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

//Hooks
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { HasPermission } from "Core/security"

//Actions
import { reset, manageSearchDriverStatus } from "./redux/slice";
import { sendGenericExport, clearRedux, savePreference } from "Redux/actions";
import { openModalDeleteManageDriverStatus } from "./modals/deleteManageDriverStatus/redux/slice";

//Controls
import {
  Container,
  IconButton,
  MenuComponent,
  SearchInput,
  TableButtons,
  TableView,
  useTablePagination,
} from "@gpstrackit/gtc-react-controls";

//Components
import { Grid } from "@mui/material";
import { NotificationManager } from "../../../../components/Notifications";
import Preferences from "Core/containers/Preferences";

//Modals
import CreateEditManageDriverStatus from "./modals/createEditManageDriverStatus";
import DeleteManageDriverStatus from "./modals/deleteManageDriverStatus";

//Constants
import {
  DEFAULT_DRIVER_STATUS_ACTIONS,
  DEFAULT_DRIVER_STATUS_NAME,
  searchDriverStatusKey,
} from "./constants";
import { openModalCreateEditManageDriverStatus } from "./modals/createEditManageDriverStatus/redux/slice";

//Helpers
import { completeColumns } from "Core/data/Helpers";
import { disableItemSelection } from "../../.globals/utils";

const KEY_PREFERENCE = "Manage-DriverStatusTab-list";

const INITIAL_SORT = { key: "id", order: "asc" };

const DriverStatusTab = () => {
  //States
  const [selectedItems, setSelectedItems] = useState([]);
  const [itemsFiltered, setItemsFiltered] = useState([]);
  const [preferences, setPreferences] = useState({});
  const [loadPreferences, setLoadPreferences] = useState(true);
  const [loadColumns, setLoadColumns] = useState(false);

  //Table refs
  const driverStatusTableRef = useRef();

  //Language
  const { messages, locale } = useIntl();

  //Redux
  const {
    loadingDriverStatusList,
    driverStatusList,
    totalDriverStatusList,
    error,
  } = useSelector((state) => state.manageDriverStatusTabRedux);
  const exportResult = useSelector((state) => state.commomRedux.exportResult);
  const exportName = useSelector((state) => state.commomRedux.exportName);
  const openCreateEditManageDriverStatus = useSelector(
    (state) => state.manageCreateEditManageDriverStatusRedux.open
  );
  const openDeleteManageDriverStatus = useSelector(
    (state) => state.manageDeleteManageDriverStatusRedux.open
  );
  const authenticatedId = useSelector(
    (state) => state.securityRedux.authenticatedId
  );
  const dispatch = useDispatch();
  const paginationDriverStatus = useTablePagination({
    dispatch,
    action: manageSearchDriverStatus,
    searchFields: ["name"],
    tableRef: driverStatusTableRef,
    sendGenericExport,
    exportName: "driverStatus",
  });

  // Permissions
  const user = useSelector((state) => state.securityRedux.user);
  const editablePermissions = HasPermission(user, "", [348])

  if (error) {
    NotificationManager.error(
      messages["errorMessage"],
      messages["error"],
      3000,
      null,
      null,
      "warning"
    );
    dispatch(reset({ error: null }));
  }

  if (exportResult && exportName === "driverStatus") {
    NotificationManager.info(
      messages["exportInProcessBody"],
      messages["exportInProcessTitle"],
      4000,
      null,
      null,
      "info",
      null
    );
    dispatch(clearRedux("COMMON", { exportResult: null, exportName: null }));
  }

  //Options
  const menuDriverStatusTabOptions = [messages["edit"], messages["delete"]];

  //Memos
  const currentColumns = useMemo(() => {
    const statusActionsTranslated = Object.entries(
      DEFAULT_DRIVER_STATUS_ACTIONS
    ).reduce((acc, key) => {
      const [status, action] = key;
      acc[status] = messages[action];
      return acc;
    }, {});
    return [
      {
        key: "name",
        title: messages["manage_driverStatusTable_driverStatusName"],
        functionString: `return ${JSON.stringify(
          statusActionsTranslated
        )}[row?.id + ""] || row?.name || ""`,
        show: true,
        export: true,
        sort: true,
        exportValue: (row) =>
          DEFAULT_DRIVER_STATUS_ACTIONS[row?.id + ""]
            ? messages[DEFAULT_DRIVER_STATUS_ACTIONS[row?.id + ""]]
            : row?.name,
        render: (row) => (
          <Grid container alignItems="center">
            <Grid item>
              <p>
                {DEFAULT_DRIVER_STATUS_ACTIONS[row?.id + ""]
                  ? messages[DEFAULT_DRIVER_STATUS_ACTIONS[row?.id + ""]]
                  : row?.name}
              </p>
            </Grid>
            <Grid item>
              {editablePermissions && [-1, -2, -3, -4, 1].includes(row?.id) === false && (
                <MenuComponent
                  options={menuDriverStatusTabOptions}
                  isDisable={loadingDriverStatusList}
                  placement="right-start"
                  icon="more_vert"
                  className="showInHover"
                  onClick={handleClickMenu}
                  value={row}
                />
              )}
            </Grid>
          </Grid>
        ),
      },
      {
        key: "accumulateTime",
        title: messages["manage_driverStatusTable_accumulateTime"],
        functionString: `return row?.accumulateTime ? "${messages["yes"]}" : "${messages["no"]}"`,
        show: true,
        export: true,
        exportValue: (row) =>
          row?.accumulateTime ? messages["yes"] : messages["no"],
        render: (row) => (
          <Grid container alignItems="center">
            <Grid item>
              <p>{row?.accumulateTime ? messages["yes"] : messages["no"]}</p>
            </Grid>
          </Grid>
        ),
      },
    ];
  }, [locale]);


  const handleClickMenu = (type, driverStatus) => {
    if (type === messages["edit"]) {
      dispatch(openModalCreateEditManageDriverStatus(driverStatus));
      return;
    }
    if (type === messages["delete"]) {
      if (driverStatus.id)
        dispatch(
          openModalDeleteManageDriverStatus({
            deleteDriverStatus: driverStatus,
          })
        );
      return;
    }
  };

  const refreshTable = useCallback(() => {
    setSelectedItems([]);
    driverStatusTableRef.current?.setPage?.(1);
    driverStatusTableRef.current?.setSelectedItems?.([]);
    paginationDriverStatus.refresh({ _page: 1 });
  }, [paginationDriverStatus]);

  const savePreferences = useCallback(
    (preferencesArray) => {
      if (!Array.isArray(preferencesArray)) {
        console.error("Expected an array of preferences");
        return;
      }

      let updatedPreferences = { ...preferences } || {};

      preferencesArray.forEach(({ key, value }) => {
        updatedPreferences[key] = value;
      });

      setPreferences(updatedPreferences);

      dispatch(
        savePreference({
          entityName: "User",
          entityId: authenticatedId,
          preferenceKey: KEY_PREFERENCE,
          preferenceValue: updatedPreferences,
        })
      );
    },
    [preferences]
  );

  const handleDeleteDriverStatusList = useCallback(() => {
    dispatch(
      openModalDeleteManageDriverStatus({
        deleteDriverStatusList: selectedItems,
      })
    );
  }, [selectedItems]);

  //Effects
  useEffect(() => {
    if (preferences?.columns?.length && !loadColumns) {
      const preferenceMap = new Map(
        preferences.columns.map((col) => [col.key, col])
      );

      // Reorder columns based on preferences (keeping `show`)
      const reorderedColumns = preferences.columns
        .map((pref) => {
          const col = currentColumns.find((c) => c.key === pref.key);
          return col ? { ...col, show: pref.show } : null;
        })
        .filter(Boolean);

      // Add new columns (not listed in preferences), with `show: true` by default
      const newColumns = currentColumns
        .filter((col) => !preferenceMap.has(col.key))
        .map((col) => ({ ...col, show: true }));

      const finalColumns = [...reorderedColumns, ...newColumns];

      paginationDriverStatus.setColumns(finalColumns);
      setPreferences({ ...preferences });
      setLoadColumns(true);
    }
  }, [preferences.columns, loadColumns]);

  useEffect(() => {
    if (paginationDriverStatus.columns.length > 0) {
      setPreferences({
        ...preferences,
        columns: paginationDriverStatus.columns,
      });
      setLoadColumns(false);
    }
  }, [locale]);


  useEffect(() => {
    if (driverStatusList.length === 0 && totalDriverStatusList === 0)
      return setItemsFiltered([]);
    setItemsFiltered(driverStatusList);
  }, [driverStatusList, totalDriverStatusList]);

  useEffect(() => {
    paginationDriverStatus.setColumns(currentColumns);
  }, [currentColumns]);

  return (
    <>
      <Container maxWidth={false}>
        <Grid
          container
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Grid item xs={6} sm={2}>
            <Grid
              container
              direction="row"
              justifyContent="flex-start"
              alignItems="center"
              spacing={1}
            >
              <Grid item style={{ display: "flex" }}>
                {editablePermissions &&
                  <>
                    <IconButton
                      tooltip={messages["add"]}
                      placement="bottom"
                      variant="secondary"
                      icon="add_box"
                      size="small"
                      isDisabled={loadingDriverStatusList}
                      onClick={() =>
                        dispatch(openModalCreateEditManageDriverStatus())
                      }
                    />
                    <IconButton
                      tooltip={messages["delete"]}
                      placement="bottom"
                      variant="secondary"
                      icon="delete"
                      size="small"
                      onClick={() => handleDeleteDriverStatusList()}
                      style={{ opacity: !selectedItems.length && "0.6" }}
                      isDisabled={!selectedItems.length}
                    />
                  </>
                }
                <TableButtons
                  messages={messages}
                  buttons={["export", "orderColumn"]}
                  {...paginationDriverStatus}
                  onChangeColumns={(columns) => {
                    savePreferences([{ key: "columns", value: columns }]);
                    paginationDriverStatus.setColumns(columns);
                  }}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} sm={4} className="mb-10 text-right">
            <SearchInput
              placeholder={messages["manage_driverStatus_search_by_name"]}
              searchText={paginationDriverStatus.searchText}
              onSearchText={paginationDriverStatus.onSearchChange}
            />
          </Grid>
        </Grid>
        <Preferences
          preferencesKey={KEY_PREFERENCE}
          onInitialPreferences={(data) => {
            if (!loadPreferences) return;
            setTimeout(() => {
              let sort = data?.sort || INITIAL_SORT; // * Initial sort
              driverStatusTableRef.current?.setSort?.(sort);
              paginationDriverStatus.onChangeSort(sort);
            }, 100);
            setPreferences(data);
            setLoadPreferences(false);
          }}
        >
          <TableView
            keyColumn="id"
            loading={loadingDriverStatusList || loadPreferences}
            topheight={235}
            disableItemSelection={disableItemSelection(paginationDriverStatus)}
            data={itemsFiltered || []}
            total={totalDriverStatusList}
            {...paginationDriverStatus}
            onChangePage={paginationDriverStatus.onChangePage}
            onChangeRowsPerPage={paginationDriverStatus.onChangeRowsPerPage}
            onSelectedItems={(newSelectedItems) => {
              const filteredSelectedItems = newSelectedItems?.filter(
                (selectedItem) => ![-1, -2, -3, -4].includes(selectedItem?.id)
              );
              setSelectedItems(filteredSelectedItems || []);
            }}
            onChangeColumns={(columns) => {
              const newcurrentColumns = paginationDriverStatus?.columns || [];
              const finalsColumns = completeColumns(newcurrentColumns, columns);
              savePreferences([{ key: "columns", value: finalsColumns }]);
              paginationDriverStatus.onChangeColumns(finalsColumns);
            }}
            onChangeSort={(newSort) => {
              savePreferences([{ key: "sort", value: newSort }]);
              paginationDriverStatus.onChangeSort(newSort);
            }}
          />
        </Preferences>
      </Container>
      {openCreateEditManageDriverStatus && (
        <CreateEditManageDriverStatus refresh={refreshTable} />
      )}
      {openDeleteManageDriverStatus && (
        <DeleteManageDriverStatus
          callback={() => {
            paginationDriverStatus.onSearchChange("");
          }}
        />
      )}
    </>
  );
};

export default DriverStatusTab;
